import React, { useEffect, useState } from 'react';

import axiosInstance from '../components/axios';
import Toast from '../components/toast';
import {
	validPasswordLength,
	validPasswordUppercase,
	validPasswordLowercase,
	validPasswordNumber,
	validPasswordSpecial,
	validPasswordNotEmail
} from './validation';

import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	TextField,
	Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { click } from '@testing-library/user-event/dist/click';
import { set } from 'date-fns';

export default function PasswordResetDialog({ emailAddress, dialogOpen, setDialogOpen }) {

	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
		clickToClose: true,
	});

	const [password, setPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [newPassword2, setNewPassword2] = useState('');

	// Password conditions
	const [passwordLength, setPasswordLength] = useState(false);
	const [passwordUpper, setPasswordUpper] = useState(false);
	const [passwordLower, setPasswordLower] = useState(false);
	const [passwordNumber, setPasswordNumber] = useState(false);
	const [passwordSpecial, setPasswordSpecial] = useState(false);
	const [passwordNotEmail, setPasswordNotEmail] = useState(false);
	const [passwordNotCurrent, setPasswordNotCurrent] = useState(false);

	const [passwordMatch, setPasswordMatch] = useState(false);

	// Password requirements checks
	const checkPasswordLength = () => { setPasswordLength(validPasswordLength(newPassword)) };

	const checkPasswordUpper = () => { setPasswordUpper(validPasswordUppercase(newPassword)) };

	const checkPasswordLower = () => { setPasswordLower(validPasswordLowercase(newPassword)) };

	const checkPasswordNumber = () => { setPasswordNumber(validPasswordNumber(newPassword)) };
	
	const checkPasswordSpecial = () => { setPasswordSpecial(validPasswordSpecial(newPassword)) };

	const checkPasswordNotEmail = () => { setPasswordNotEmail(validPasswordNotEmail(newPassword, emailAddress)) };

	const checkPasswordNotCurrent = () => { setPasswordNotCurrent(newPassword && newPassword !== password) };
	
	// Password match check
	const checkPasswordMatch = () => { setPasswordMatch(newPassword && newPassword2 && newPassword === newPassword2) };

	// Verify all password requirements are met
	const verifyPassRequirementsMet = passwordLength && passwordUpper && passwordLower && passwordNumber && passwordSpecial && passwordNotEmail && passwordNotCurrent;

	// Check if new password meets all requirements
	useEffect(() => {
		checkPasswordLength();
		checkPasswordUpper();
		checkPasswordLower();
		checkPasswordNumber();
		checkPasswordSpecial();
		checkPasswordNotEmail();
		checkPasswordNotCurrent();
	}, [newPassword]);
	
	useEffect(() => {
		checkPasswordNotCurrent();
	}, [password]);

	// Check if both new password entries match
	useEffect(() => {
		checkPasswordMatch();
	}, [newPassword, newPassword2]);

	// Clear the contents of the dialog
	const clearDialog = () => {
		setPassword('');
		setNewPassword('');
		setNewPassword2('');
	};

	// Call self password reset API endpoint
	const resetPassword = () => {
		axiosInstance
			.post(`users/selfpassreset/`, {
				current_password: password,
				new_password: newPassword,
			})
			.then((res) => {
				setToast({
					open: true,
					severity: 'success',
					message: 'Password reset successful.',
					clickToClose: true,
				});
			})
			.catch((error) => {
				setToast({
					open: true,
					severity: 'error',
					message: 'Password reset failed. ' + error,
					clickToClose: true,
				});
				console.log(`Password reset error: ${error}`);
			});
	};

	// Handle dialog closing
	const handleClose = () => {
		setDialogOpen(false);
		clearDialog();
	};

	// Handle button clicks
	const handleSaveClick = () => {
		if (verifyPassRequirementsMet && passwordMatch) {
			resetPassword();
			handleClose();
		} else if (!verifyPassRequirementsMet) {
			setToast({
				open: true,
				severity: 'error',
				message: 'Your new password does not meet minimum password requirements.',
				clickToClose: true,
			});
		} else if (!passwordMatch) {
			setToast({
				open: true,
				severity: 'error',
				message: 'Passwords do not match.',
				clickToClose: true,
			});
		}
	};

	const handleCancelClick = () => {
		handleClose();
	};

	// Handle textfield password updates
	const handlePasswordChange = (event) => {
		setPassword(event.target.value);
	};

	const handleNewPasswordChange = (event) => {
		setNewPassword(event.target.value);
	};

	const handleNewPassword2Change = (event) => {
		setNewPassword2(event.target.value);
	};

	return (
		<>
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
				clickToClose={toast.clickToClose}
			/>
			<Dialog
				open={dialogOpen}
				onClose={handleClose}
				aria-labelledby="form-dialog-title"
				maxWidth='xs'
				sx={{
					'.MuiDialog-paper': {
						border: '5px solid #B1282E',
						borderRadius: 3
					},
				}}
			>
				<DialogTitle id="form-dialog-title">
					<Typography variant="h5" component="div" fontFamily='Saira, sans-serif' fontWeight={600} textAlign='center' sx={{ flexGrow: 1 }}>
						Set New Password
					</Typography>
				</DialogTitle>
				<DialogContent>
					<TextField
						disabled
						margin="dense"
						id="email"
						label="Email"
						fullWidth
						value={emailAddress}
						inputProps={{ maxLength: 128 }}
					/>
					<TextField
						autoFocus
						margin="dense"
						id="current_password"
						label="Current Password"
						type="password"
						fullWidth
						value={password}
						onChange={handlePasswordChange}
						inputProps={{ maxLength: 32 }}
					/>
					<TextField
						margin="dense"
						id="new_password"
						label="New Password"
						type="password"
						fullWidth
						error={newPassword.length > 0 && !verifyPassRequirementsMet}
						helperText={(newPassword.length > 0 && !verifyPassRequirementsMet) ? 'Password does not meet minimum requirements' : ''}
						value={newPassword}
						onChange={handleNewPasswordChange}
						inputProps={{ maxLength: 32 }}
					/>
					<Box sx={{ m: '20px' }}>
						<Typography variant="h7" component="div" sx={{ flexGrow: 1 }}>
							Password Requirements:
						</Typography>
						<List>
							<ListItem sx={{ paddingY: 0 }}>
								{passwordLength ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
								<ListItemText sx={{ paddingLeft: 2 }} primary="At least 8 characters long" />
							</ListItem>
							<ListItem sx={{ paddingY: 0 }}>
								{passwordUpper ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
								<ListItemText sx={{ paddingLeft: 2 }} primary="At least 1 uppercase letter" />
							</ListItem>
							<ListItem sx={{ paddingY: 0 }}>
								{passwordLower ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
								<ListItemText sx={{ paddingLeft: 2 }} primary="At least 1 lowercase letter" />
							</ListItem>
							<ListItem sx={{ paddingY: 0 }}>
								{passwordNumber ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
								<ListItemText sx={{ paddingLeft: 2 }} primary="At least 1 number" />
							</ListItem>
							<ListItem sx={{ paddingY: 0 }}>
								{passwordSpecial ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
								<ListItemText sx={{ paddingLeft: 2 }} primary="At least 1 special character" />
							</ListItem>
							<ListItem sx={{ paddingY: 0 }}>
								{passwordNotEmail ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
								<ListItemText sx={{ paddingLeft: 2 }} primary="Not your email" />
							</ListItem>
							<ListItem sx={{ paddingY: 0 }}>
								{passwordNotCurrent ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
								<ListItemText sx={{ paddingLeft: 2 }} primary="Not your current password" />
							</ListItem>
						</List>
					</Box>
					<TextField
						margin="dense"
						id="new_password2"
						label="Confirm New Password"
						type="password"
						fullWidth
						error={newPassword2.length > 0 && !passwordMatch}
						helperText={(newPassword2.length > 0 && !passwordMatch) ? 'Passwords do not match' : ''}
						value={newPassword2}
						onChange={handleNewPassword2Change}
						inputProps={{ maxLength: 32 }}
					/>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center', paddingBottom: 3 }}>
					<Button onClick={handleCancelClick} variant='outlined' size='large'>
						Cancel
					</Button>
					<Button onClick={handleSaveClick} variant='contained' size='large' disabled={!verifyPassRequirementsMet || !passwordMatch}>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}